import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaCar, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaHome, FaSuitcase, FaMoneyBillWave, FaInfoCircle, FaWhatsapp, FaPrint } from "react-icons/fa";
import { BASE_URL } from "../utils/url.js";

const Details = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/bookings/get/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch booking details');
        }
        const data = await response.json();
        setBooking(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id]);

  const formatDate = (dateString) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  };

  const formatDateForWhatsApp = (dateString) => {
    const options = { 
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  };

  const shareViaWhatsApp = () => {
    if (!booking) return;

    const pickupAddress = `${booking.pickupDetails.houseNo} ${booking.pickupDetails.street}, ${booking.pickupDetails.town}, ${booking.pickupDetails.postalCode}`;
    const destinationAddress = `${booking.destinationDetails.houseNo} ${booking.destinationDetails.street}, ${booking.destinationDetails.town}, ${booking.destinationDetails.postalCode}`;
    
    const message = `🚕 *Watford Taxi Booking Confirmation*

*Booking ID:* ${booking.bookingId}
*Passenger:* ${booking.title} ${booking.name}
*Phone:* ${booking.phoneNumber}

📍 *Pickup:* ${pickupAddress}

🎯 *Destination:* ${destinationAddress}

📅 *Date & Time:* ${formatDateForWhatsApp(booking.dateBooked)}
🚗 *Vehicle:* ${booking.vehicle?.title || 'Standard Vehicle'}

💰 *Total Price:* £${booking.price?.toFixed(2) || 'To be confirmed'}

📝 *Special Instructions:* ${booking.instructions || 'None'}

Thank you for choosing Watford Taxi! For any changes, please contact us at 01923 803603.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const downloadAsTextFile = () => {
    if (!booking) return;

    const pickupAddress = `${booking.pickupDetails.houseNo} ${booking.pickupDetails.street}, ${booking.pickupDetails.town}, ${booking.pickupDetails.postalCode}`;
    const destinationAddress = `${booking.destinationDetails.houseNo} ${booking.destinationDetails.street}, ${booking.destinationDetails.town}, ${booking.destinationDetails.postalCode}`;
    
    const content = `Watford Taxi Booking Confirmation
=========================================

Booking ID: ${booking.bookingId}
Passenger: ${booking.title} ${booking.name}
Email: ${booking.email}
Phone: ${booking.phoneNumber}

PICKUP DETAILS:
Address: ${pickupAddress}

DESTINATION DETAILS:
Address: ${destinationAddress}

TRIP DETAILS:
Date & Time: ${formatDate(booking.dateBooked)}
Vehicle: ${booking.vehicle?.title || 'Standard Vehicle'}
Capacity: ${booking.vehicle?.pass} passengers, ${booking.vehicle?.suit} suitcases

PAYMENT DETAILS:
Total Price: £${booking.price?.toFixed(2) || 'To be confirmed'}
Booking Date: ${formatDate(booking.createdAt)}
Status: Confirmed

SPECIAL INSTRUCTIONS:
${booking.instructions || 'No special instructions provided'}

Contact: info@watfordtaxi.co.uk | 01923 803603
Booking created on: ${formatDate(booking.createdAt)}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `watford-taxi-booking-${booking.bookingId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-dark">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Error Loading Booking</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-dark">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full text-center">
          <h2 className="text-xl font-semibold mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-4">The requested booking could not be found.</p>
          <button
            onClick={() => navigate("/")}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        {/* Header */}
        <div className="bg-primary p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold">Booking Confirmation</h1>
              <p className="text-gray-900 font-bold">Booking ID: {booking.bookingId}</p>
            </div>
            <div className="bg-white text-dark px-3 py-1 rounded-full text-sm font-semibold">
              {booking.vehicle?.title || 'Standard Vehicle'}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 md:p-8">
          {/* Booking Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-dark p-4 rounded-lg">
              <h2 className="flex items-center text-lg font-semibold mb-3">
                <FaMapMarkerAlt className="mr-2 text-blue-500" />
                Pickup Details
              </h2>
              <div className="space-y-2">
                <p><span className="font-medium">Address:</span> {booking.pickupDetails.houseNo} {booking.pickupDetails.street}</p>
                <p><span className="font-medium">Town:</span> {booking.pickupDetails.town}</p>
                <p><span className="font-medium">Postal Code:</span> {booking.pickupDetails.postalCode}</p>
              </div>
            </div>

            <div className="bg-dark p-4 rounded-lg">
              <h2 className="flex items-center text-lg font-semibold mb-3">
                <FaMapMarkerAlt className="mr-2 text-green-500" />
                Destination Details
              </h2>
              <div className="space-y-2">
                <p><span className="font-medium">Address:</span> {booking.destinationDetails.houseNo} {booking.destinationDetails.street}</p>
                <p><span className="font-medium">Town:</span> {booking.destinationDetails.town}</p>
                <p><span className="font-medium">Postal Code:</span> {booking.destinationDetails.postalCode}</p>
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-dark p-4 rounded-lg">
              <h2 className="flex items-center text-lg font-semibold mb-3">
                <FaCalendarAlt className="mr-2 text-purple-500" />
                Trip Details
              </h2>
              <div className="space-y-2">
                <p><span className="font-medium">Date & Time:</span> {formatDate(booking.dateBooked)}</p>
                <p><span className="font-medium">Vehicle:</span> {booking.vehicle?.title}</p>
                <p><span className="font-medium">Capacity:</span> {booking.vehicle?.pass} passengers, {booking.vehicle?.suit} suitcases</p>
              </div>
            </div>

            <div className="bg-dark p-4 rounded-lg">
              <h2 className="flex items-center text-lg font-semibold mb-3">
                <FaUser className="mr-2 text-yellow-500" />
                Passenger Details
              </h2>
              <div className="space-y-2">
                <p><span className="font-medium">Name:</span> {booking.title} {booking.name}</p>
                <p><span className="font-medium">Email:</span> {booking.email}</p>
                <p><span className="font-medium">Phone:</span> {booking.phoneNumber}</p>
              </div>
            </div>

            <div className="bg-dark p-4 rounded-lg">
              <h2 className="flex items-center text-lg font-semibold mb-3">
                <FaMoneyBillWave className="mr-2 text-green-500" />
                Payment Details
              </h2>
              <div className="space-y-2">
                <p><span className="font-medium">Total Price:</span> £{booking.price?.toFixed(2) || 'To be confirmed'}</p>
                <p><span className="font-medium">Booking Date:</span> {formatDate(booking.createdAt)}</p>
                <p><span className="font-medium">Status:</span> Confirmed</p>
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          <div className="bg-dark p-4 rounded-lg mb-8">
            <h2 className="flex items-center text-lg font-semibold mb-3">
              <FaInfoCircle className="mr-2 text-blue-500" />
              Special Instructions
            </h2>
            <p>{booking.instructions || 'No special instructions provided'}</p>
          </div>

          {/* Vehicle Details */}
          {booking.vehicle && (
            <div className="bg-dark p-4 rounded-lg mb-8">
              <h2 className="flex items-center text-lg font-semibold mb-3">
                <FaCar className="mr-2 text-blue-500" />
                Vehicle Information
              </h2>
              <div className="flex flex-col md:flex-row gap-6">
                {booking.vehicle.carImg && (
                  <div className="md:w-1/3">
                    <img 
                      src={booking.vehicle.carImg} 
                      alt={booking.vehicle.title} 
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                )}
                <div className="md:w-2/3">
                  <h3 className="text-lg font-semibold">{booking.vehicle.title}</h3>
                  <p className="text-gray-600 mb-3">{booking.vehicle.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <FaUser className="mr-2 text-gray-500" />
                      <span>{booking.vehicle.pass} passengers</span>
                    </div>
                    <div className="flex items-center">
                      <FaSuitcase className="mr-2 text-gray-500" />
                      <span>{booking.vehicle.suit} suitcases</span>
                    </div>
                    <div className="flex items-center">
                      <FaHome className="mr-2 text-gray-500" />
                      <span>{booking.vehicle.hand} hand luggage</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8">
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition flex items-center justify-center"
            >
              Back to Home
            </button>
            <button
              onClick={downloadAsTextFile}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center justify-center"
            >
              <FaPrint className="mr-2" />
              Download as Text
            </button>
            <button
              onClick={shareViaWhatsApp}
              className="px-6 py-3 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-lg font-medium transition flex items-center justify-center"
            >
              <FaWhatsapp className="mr-2 text-lg" />
              Share via WhatsApp
            </button>
            <button
              onClick={() => window.print()}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition flex items-center justify-center"
            >
              <FaPrint className="mr-2" />
              Print Booking
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-4 text-center text-sm text-gray-600">
          <p>Need help? Contact us at info@watfordtaxi.co.uk or call 01923 803603</p>
          <p className="mt-1">Booking created on {formatDate(booking.createdAt)}</p>
        </div>
      </div>
    </div>
  );
};

export default Details;